import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnDestroy, Output, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, Subject, catchError, debounceTime, distinctUntilChanged, finalize, of, takeUntil } from 'rxjs';

export interface PersonFilterOption { id: string; fullName: string; description?: string; }
export interface PersonFilterPage { items: PersonFilterOption[]; totalCount: number; pageNumber: number; hasNextPage: boolean; }
export type PersonFilterSelection = string[] | null;

@Component({
  selector: 'app-person-multi-select-filter',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatCheckboxModule, MatFormFieldModule, MatInputModule, MatProgressSpinnerModule],
  templateUrl: './person-multi-select-filter.component.html',
  styleUrl: './person-multi-select-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PersonMultiSelectFilterComponent), multi: true }],
})
export class PersonMultiSelectFilterComponent implements ControlValueAccessor, OnDestroy {
  @Input({ required: true }) label = '';
  @Input({ required: true }) searchPeople!: (term: string, page: number) => Observable<PersonFilterPage>;
  @Output() selectedPeopleChange = new EventEmitter<PersonFilterOption[]>();

  readonly search = new FormControl('', { nonNullable: true });
  results: PersonFilterOption[] = [];
  selectedPeople: PersonFilterOption[] = [];
  loading = false;
  hasNextPage = false;
  totalCount = 0;
  panelOpen = false;
  disabled = false;
  error = false;
  selection: PersonFilterSelection = null;
  private readonly peopleById = new Map<string, PersonFilterOption>();
  private currentTerm = '';
  private currentPage = 1;
  private requestVersion = 0;
  private onChange: (value: PersonFilterSelection) => void = () => {};
  private onTouched: () => void = () => {};
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly cdr: ChangeDetectorRef, private readonly host: ElementRef<HTMLElement>) {
    this.search.valueChanges.pipe(debounceTime(150), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(term => { if (this.panelOpen) this.loadPage(term.trim(), 1, false); });
  }

  @HostBinding('class.person-filter--open')
  get isOpen(): boolean { return this.panelOpen; }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  writeValue(value: PersonFilterSelection): void {
    this.selection = value === null ? null : [...new Set(value ?? [])];
    this.refreshSelectedPeople();
    this.cdr.markForCheck();
  }

  registerOnChange(onChange: (value: PersonFilterSelection) => void): void { this.onChange = onChange; }
  registerOnTouched(onTouched: () => void): void { this.onTouched = onTouched; }
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    if (disabled) { this.panelOpen = false; this.requestVersion++; this.loading = false; }
    this.cdr.markForCheck();
  }

  togglePanel(): void {
    if (this.disabled) return;
    this.panelOpen = !this.panelOpen;
    this.onTouched();
    if (this.panelOpen) this.loadPage(this.search.value.trim(), 1, false);
    else { this.requestVersion++; this.loading = false; }
    this.cdr.markForCheck();
  }

  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(event: PointerEvent): void {
    if (this.panelOpen && event.target instanceof Node && !this.host.nativeElement.contains(event.target)) {
      this.togglePanel();
    }
  }

  selectAll(): void { if (!this.disabled) { this.selection = null; this.commit(); } }
  selectNone(): void { if (!this.disabled) { this.selection = []; this.commit(); } }

  toggle(person: PersonFilterOption, selected: boolean): void {
    if (this.disabled) return;
    this.peopleById.set(person.id, person);
    this.selection = selected
      ? [...new Set([...(this.selection ?? []), person.id])]
      : (this.selection ?? []).filter(id => id !== person.id);
    this.commit();
  }

  isSelected(id: string): boolean { return this.selection?.includes(id) ?? false; }
  get selectionLabel(): string {
    if (this.selection === null) return 'Todos';
    if (!this.selection.length) return 'Ninguno';
    return this.selection.map(id => this.peopleById.get(id)?.fullName ?? id).join(', ');
  }

  loadNextPage(): void {
    if (!this.loading && this.hasNextPage) this.loadPage(this.currentTerm, this.currentPage + 1, true);
  }

  private loadPage(term: string, page: number, append: boolean): void {
    const version = ++this.requestVersion;
    this.loading = true;
    this.error = false;
    if (!append) { this.results = []; this.hasNextPage = false; this.totalCount = 0; }
    this.cdr.markForCheck();
    this.searchPeople(term, page).pipe(
      catchError(() => of(null)),
      finalize(() => { if (version === this.requestVersion) { this.loading = false; this.cdr.markForCheck(); } }),
      takeUntil(this.destroy$),
    ).subscribe(response => {
      if (version !== this.requestVersion) return;
      if (!response) { this.error = true; return; }
      this.currentTerm = term;
      this.currentPage = response.pageNumber;
      response.items.forEach(person => this.peopleById.set(person.id, person));
      this.results = append
        ? [...new Map([...this.results, ...response.items].map(person => [person.id, person])).values()]
        : response.items;
      this.refreshSelectedPeople();
      this.orderSelectedFirst();
      this.hasNextPage = response.hasNextPage;
      this.totalCount = response.totalCount;
      this.selectedPeopleChange.emit(this.selectedPeople);
      this.cdr.markForCheck();
    });
  }

  private refreshSelectedPeople(): void {
    this.selectedPeople = (this.selection ?? []).map(id => this.peopleById.get(id) ?? { id, fullName: id });
  }
  private orderSelectedFirst(): void {
    this.results = [...this.results].sort((a, b) => Number(this.isSelected(b.id)) - Number(this.isSelected(a.id)));
  }
  private commit(): void {
    this.refreshSelectedPeople();
    this.orderSelectedFirst();
    this.selectedPeopleChange.emit(this.selectedPeople);
    this.onChange(this.selection === null ? null : [...this.selection]);
    this.onTouched();
    this.cdr.markForCheck();
  }
}
